import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { toastError } from "../../shared/toastHelper";
import StatusBadge from "../../shared/StatusBadge";
import SeriesIndex from "./components/SeriesIndex";
import SeriesNameCell from "./components/cells/SeriesNameCell";
import ScheduleCell from "./components/cells/ScheduleCell";
import DateRangeCell from "./components/cells/DateRangeCell";
import EventsCountCell from "./components/cells/EventsCountCell";
import "../TeamManager/styles/teamManagerStyles.css";
import "./styles/seriesIndexStyles.css";

const SeriesIndexContext = createContext();

export function useSeriesIndexContext() {
  return useContext(SeriesIndexContext);
}

const STATUS_LABELS = { active: "Active", draft: "Draft", archived: "Archived", canceled: "Cancelled" };
const STATUS_BADGE_KEY = { active: "active", archived: "paused", canceled: "canceled" };

const COLUMNS = [
  { data: "series", label: "Series" },
  { data: "status", label: "Status", orderable: false },
  { data: "schedule", label: "Schedule", orderable: false },
  { data: "date_range", label: "Date range" },
  { data: "events_count", label: "Events", orderable: false },
];

const SeriesIndexProvider = (props) => {
  const { teamId, datatablePath, totalCount, activeCount, draftCount, setupWizardEnabled,
    canCreateSeries, venueOwnerships, collectionsPath } = props;

  const archivedCount = totalCount - activeCount - draftCount;

  const [activeTab, setActiveTab] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");

  const dataSource = useMemo(() => {
    const params = new URLSearchParams({ state: activeTab });
    if (searchTerm.trim()) params.set("q", searchTerm.trim());
    const separator = datatablePath.includes("?") ? "&" : "?";
    return `${datatablePath}${separator}${params.toString()}`;
  }, [datatablePath, activeTab, searchTerm]);

  const [creatingDraft, setCreatingDraft] = useState(false);

  const createDraft = async () => {
    if (creatingDraft) return;
    setCreatingDraft(true);
    try {
      const { data } = await axios.post(`/teams/${teamId}/event_series`, {
        wizard: true,
        event_series: { name: "Untitled series" },
      });
      window.location = `/teams/${teamId}/event_series/${data.id}/edit`;
    } catch (e) {
      toastError("Could not start a new series. Please try again.");
      setCreatingDraft(false);
    }
  };

  const rowCallback = useMemo(
    () => (row, rowData) => {
      const seriesEl = row.getElementsByClassName("series-cell")[0];
      if (seriesEl) {
        ReactDOM.render(
          <SeriesNameCell
            name={rowData.name}
            venueName={rowData.venue_name}
            imageUrl={rowData.source_image_url}
            showUrl={rowData.show_url}
          />,
          seriesEl
        );
      }

      const statusEl = row.getElementsByClassName("status-cell")[0];
      if (statusEl) {
        ReactDOM.render(
          <StatusBadge
            status={STATUS_BADGE_KEY[rowData.state_value] || rowData.state_value}
            label={STATUS_LABELS[rowData.state_value] || rowData.state_value}
          />,
          statusEl
        );
      }

      const scheduleEl = row.getElementsByClassName("schedule-cell")[0];
      if (scheduleEl) {
        const daysOfWeek = typeof rowData.days_of_week === "string"
          ? JSON.parse(rowData.days_of_week)
          : rowData.days_of_week;
        ReactDOM.render(<ScheduleCell daysOfWeek={daysOfWeek} />, scheduleEl);
      }

      const dateRangeEl = row.getElementsByClassName("date-range-cell")[0];
      if (dateRangeEl) {
        ReactDOM.render(
          <DateRangeCell startDate={rowData.start_date} endDate={rowData.end_date} />,
          dateRangeEl
        );
      }

      const eventsEl = row.getElementsByClassName("events-count-cell")[0];
      if (eventsEl) {
        ReactDOM.render(
          <EventsCountCell
            totalEvents={rowData.total_events}
            upcomingCount={rowData.upcoming_count}
            pastCount={rowData.past_count}
          />,
          eventsEl
        );
      }
    },
    []
  );

  const value = {
    teamId,
    datatablePath,
    totalCount,
    activeCount,
    archivedCount,
    draftCount,
    setupWizardEnabled,
    canCreateSeries,
    venueOwnerships,
    collectionsPath,
    createDraft,
    creatingDraft,
    activeTab,
    setActiveTab,
    searchTerm,
    setSearchTerm,
    dataSource,
    rowCallback,
    columns: COLUMNS,
  };

  return (
    <SeriesIndexContext.Provider value={value}>
      {props.children}
    </SeriesIndexContext.Provider>
  );
};

SeriesIndexProvider.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  datatablePath: PropTypes.string.isRequired,
  totalCount: PropTypes.number,
  activeCount: PropTypes.number,
  draftCount: PropTypes.number,
  setupWizardEnabled: PropTypes.bool,
  canCreateSeries: PropTypes.bool,
  venueOwnerships: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ),
  collectionsPath: PropTypes.string,
  children: PropTypes.node,
};

const SeriesIndexApp = ({
  teamId,
  datatablePath,
  totalCount = 0,
  activeCount = 0,
  draftCount = 0,
  setupWizardEnabled = false,
  canCreateSeries = false,
  venueOwnerships = [],
  collectionsPath,
  csrfToken,
}) => {
  // This page mutates (creates series), so set the app-wide CSRF header once —
  // the SeriesIndex page has no other global axios setup to rely on.
  useEffect(() => {
    if (csrfToken) axios.defaults.headers.common["X-CSRF-Token"] = csrfToken;
  }, [csrfToken]);

  return (
    <SeriesIndexProvider
      teamId={teamId}
      datatablePath={datatablePath}
      totalCount={totalCount}
      activeCount={activeCount}
      draftCount={draftCount}
      setupWizardEnabled={setupWizardEnabled}
      canCreateSeries={canCreateSeries}
      venueOwnerships={venueOwnerships}
      collectionsPath={collectionsPath}
    >
      <SeriesIndex />
      {/* Every bundle that calls toastError must mount its own container. */}
      <ToastContainer icon={false} />
    </SeriesIndexProvider>
  );
};

SeriesIndexApp.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  datatablePath: PropTypes.string.isRequired,
  totalCount: PropTypes.number,
  activeCount: PropTypes.number,
  draftCount: PropTypes.number,
  setupWizardEnabled: PropTypes.bool,
  canCreateSeries: PropTypes.bool,
  venueOwnerships: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ),
  collectionsPath: PropTypes.string,
  csrfToken: PropTypes.string,
};

export default SeriesIndexApp;
